/**
 * Sales team picks on the Users page (Warren, 20 October 2026).
 *
 * A sales role is only ever recorded on the Sales Staff page, with the
 * manager and the dated start, so nobody holds one without either. The
 * Users page therefore never saves a sales pick: it hands the person to the
 * Sales Staff add form, prefilled. The person travels in sessionStorage,
 * never in the URL, is read once and expires after ten minutes.
 */

export type SalesTeamPick = 'sp_manager' | 'sales_manager';

export interface SalesTeamPickOption {
  pick: SalesTeamPick;
  label: string;
  /** SalesStaff.role the add form starts on. Contractor or employee is the form's own field (default contractor). */
  role: 'rep' | 'manager';
}

/** Two entries (Warren, 20 October 2026): SP Manager is the rep role, and the Admin UI's name for it. */
export const SALES_TEAM_PICKS: readonly SalesTeamPickOption[] = [
  { pick: 'sp_manager', label: 'SP Manager', role: 'rep' },
  { pick: 'sales_manager', label: 'Sales Manager', role: 'manager' },
];

/** Option values in the Users role picker; never a role id, so never sent to the Api. */
export const SALES_TEAM_OPTION_PREFIX = 'sales-team:';

export function salesTeamOptionValue(pick: SalesTeamPick): string {
  return SALES_TEAM_OPTION_PREFIX + pick;
}

export function salesTeamPickFromValue(value: string): SalesTeamPickOption | null {
  if (!value.startsWith(SALES_TEAM_OPTION_PREFIX)) return null;
  const pick = value.slice(SALES_TEAM_OPTION_PREFIX.length);
  return SALES_TEAM_PICKS.find((p) => p.pick === pick) ?? null;
}

export interface SalesStaffPrefill {
  email: string;
  name: string;
  pick: SalesTeamPick;
  role: 'rep' | 'manager';
  /** Epoch ms when the Users page handed the person over. */
  at: number;
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export const SALES_STAFF_PREFILL_KEY = 'evi.salesStaffPrefill';
export const SALES_STAFF_PREFILL_TTL_MS = 10 * 60_000;

function sessionStore(): StorageLike | null {
  try { return window.sessionStorage; } catch { return null; }
}

/** False when the browser refuses storage; the caller then says so instead of navigating. */
export function saveSalesStaffPrefill(
  prefill: Omit<SalesStaffPrefill, 'at'>,
  store: StorageLike | null = sessionStore(),
  now = Date.now(),
): boolean {
  if (!store) return false;
  try {
    store.setItem(SALES_STAFF_PREFILL_KEY, JSON.stringify({ ...prefill, at: now }));
    return true;
  } catch {
    return false;
  }
}

/** The handed-over person, or null when there is none, it is malformed or it is stale. Does not clear it. */
export function readSalesStaffPrefill(store: StorageLike | null = sessionStore(), now = Date.now()): SalesStaffPrefill | null {
  if (!store) return null;
  let raw: unknown;
  try { raw = JSON.parse(store.getItem(SALES_STAFF_PREFILL_KEY) ?? 'null'); } catch { return null; }
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Partial<SalesStaffPrefill>;
  const option = SALES_TEAM_PICKS.find((o) => o.pick === p.pick);
  if (!option || typeof p.email !== 'string' || !p.email.includes('@') || typeof p.at !== 'number') return null;
  if (now - p.at > SALES_STAFF_PREFILL_TTL_MS || p.at > now + 60_000) return null;
  return {
    email: p.email,
    name: typeof p.name === 'string' ? p.name : '',
    pick: option.pick,
    role: p.role === 'manager' || p.role === 'rep' ? p.role : option.role,
    at: p.at,
  };
}

export function clearSalesStaffPrefill(store: StorageLike | null = sessionStore()): void {
  try { store?.removeItem(SALES_STAFF_PREFILL_KEY); } catch { /* storage refused: nothing to clear */ }
}
